import { Injectable } from "@angular/core";
import { IOstImage } from "../models/ost/ost-images/ost-images.interface";
import { IndexedDBDatabaseService } from "./indexed-db-database.service";
import { OST_CREATION_IMAGE_STORE, PERSISTENT_OST_UPLOAD_STORES } from "./indexed-db.schema";

@Injectable({ providedIn: "root" })
export class IndexedDBService {
  constructor(private readonly database: IndexedDBDatabaseService) {}

  async saveImage(image: File): Promise<boolean> {
    try {
      const db = await this.database.openDatabase();
      const content = await this.readFileContent(image);
      const transaction = db.transaction([OST_CREATION_IMAGE_STORE], "readwrite");
      const completed = this.database.waitForTransaction(transaction);
      const data: IOstImage = {
        name: image.name,
        size: image.size,
        type: image.type,
        content,
        lastModified: image.lastModified,
      };

      transaction.objectStore(OST_CREATION_IMAGE_STORE).put(data);
      await completed;
      return true;
    } catch (error) {
      console.error("Error saving image to IndexedDB:", error);
      return false;
    }
  }

  async getAllImages(): Promise<IOstImage[]> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_CREATION_IMAGE_STORE], "readonly");
    const store = transaction.objectStore(OST_CREATION_IMAGE_STORE);

    return new Promise((resolve: (value: IOstImage[]) => void, reject: (reason: unknown) => void) => {
      const request = store.getAll();
      request.onsuccess = (): void => resolve(request.result as IOstImage[]);
      request.onerror = (): void => reject([]);
    });
  }

  async getAllOstCreationImagesAsFiles(): Promise<File[]> {
    const images = await this.getAllImages();
    return images.map(
      (image: IOstImage) =>
        new File([new Blob([image.content as ArrayBuffer], { type: image.type })], image.name, {
          type: image.type,
          lastModified: image.lastModified ?? Date.now(),
        })
    );
  }

  async deleteImage(name: string): Promise<boolean> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_CREATION_IMAGE_STORE], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    transaction.objectStore(OST_CREATION_IMAGE_STORE).delete(name);
    await completed;
    return true;
  }

  async clearOstImagesStore(): Promise<boolean> {
    const db = await this.database.openDatabase();
    const transaction = db.transaction([OST_CREATION_IMAGE_STORE], "readwrite");
    const completed = this.database.waitForTransaction(transaction);
    transaction.objectStore(OST_CREATION_IMAGE_STORE).clear();
    await completed;
    return true;
  }

  async clearAll(): Promise<void> {
    const db = await this.database.openDatabase();
    const persistentUploadStores = new Set<string>(PERSISTENT_OST_UPLOAD_STORES);
    const storeNames = Array.from(db.objectStoreNames).filter((storeName: string): boolean => !persistentUploadStores.has(storeName));

    await Promise.all(
      storeNames.map(async (storeName: string): Promise<void> => {
        const transaction = db.transaction(storeName, "readwrite");
        const completed = this.database.waitForTransaction(transaction);
        transaction.objectStore(storeName).clear();
        await completed;
      })
    );
  }

  private readFileContent(file: File): Promise<ArrayBuffer | string | null> {
    return new Promise((resolve: (value: ArrayBuffer | string | null) => void, reject: (reason: unknown) => void) => {
      const reader = new FileReader();
      reader.onloadend = (): void => resolve(reader.result);
      reader.onerror = (): void => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }
}
